import { IAppSettings } from '@msdyn365-commerce/core';

/**
 * Appelle l'Azure Function et retourne:
 * ''  => configuration / URL manquante ou mal formée
 * 'N/A' => Azure Function appelée mais erreur réponse
 * <string url> => lien PDF
 */
export const getTdsUrl = async (appSettings: IAppSettings, sku: string, lang: string, timeoutMs = 8000): Promise<string> => {
    const base = appSettings?.config?.tdsAzureFnUrl?.trim();
    const apiKey = appSettings?.config?.tdsAzureFnApiKey?.trim();
    if (!base || !apiKey) {
        return '';
    }

    let url: string;
    try {
        const u = new URL(base);
        u.searchParams.set('productId', sku);
        // u.searchParams.set('productId', 'test');
        u.searchParams.set('language', lang);
        url = u.toString();
    } catch {
        return '';
    }

    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const resp = await fetch(url, {
            method: 'GET',
            signal: controller.signal,
            headers: apiKey ? { 'x-functions-key': apiKey } : {}
        });
        clearTimeout(tid);
        if (!resp.ok) {
            // console.error('TDS Azure Function response not ok:', resp.status, resp.statusText);
            return 'N/A';
        }
        const json = await resp.json();
        const downloadUrl = json?.downloadurl ?? json?.downloadUrl;
        if (!downloadUrl || typeof downloadUrl !== 'string') {
            // console.error('Invalid JSON structure, expected { "downloadurl": "<url>" }');
            return 'N/A';
        }
        return downloadUrl.trim();
    } catch (e) {
        // console.error('Error calling TDS Azure Function:', e);
        if ((e as any).name === 'AbortError') {
            return 'N/A';
        }
        return 'N/A';
    }
};

/**
 * Orchestrateur réutilisable: gère les trois cas.
 * showPopup(message, type) doit être fourni (type: 'warning' | 'info' | 'error').
 * messageOnUnavailable: resources.tdsUnavailableMessage,
 * messageOnConfigError: resources.tdsConfigErrorMessage,
 * messageDocumentReady: resources.tdsDocumentReadyMessage*
 */
export const handleTdsDocument = async (
    appSettings: IAppSettings,
    sku: string,
    locale: string,
    showPopup: (type: 'warning' | 'info' | 'error', action: string) => void,
    handlePopupMessage: (message: string) => void,
    handlePopupAction: (action: string) => void,
    openStrategy: 'direct' | 'deferred' = 'direct',
    messageOnUnavailable: string,
    messageOnConfigError: string,
    messageDocumentReady: string
): Promise<void> => {
    const lang = locale === 'nl' ? 'nl' : locale === 'fr' ? 'fr' : 'en';
    const result = await getTdsUrl(appSettings, sku, lang);
    if (result === '') {
        showPopup('warning', '');
        handlePopupMessage(messageOnConfigError);
        handlePopupAction('');
        return;
    }
    if (result === 'N/A') {
        showPopup('info', '');
        handlePopupMessage(messageOnUnavailable);
        handlePopupAction('');
        return;
    }

    // result = URL PDF
    if (openStrategy === 'direct') {
        // Téléchargement direct sans ouvrir d'onglet
        try {
            await downloadViaAnchor(result);
        } catch (e) {
            // sinon, fallback vers ouverture dans un nouvel onglet
            // console.error('Download failed, fallback to window.open:', e);
            window.open(result, '_blank', 'noopener,noreferrer');
        }
    } else {
        // Variante: popup avec lien cliquable
        showPopup('info', result);
        handlePopupMessage(messageDocumentReady);
        handlePopupAction(result);
    }
};

/** Téléchargement via <a>; */
function downloadViaAnchor(fileUrl: string): Promise<void> {
    return new Promise(resolve => {
        const a = document.createElement('a');
        a.href = fileUrl; // URL SAS complète
        a.setAttribute('download', '');
        a.rel = 'noopener';
        a.target = '_self';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        resolve();
    });
}
